import React, { useState } from 'react';
import { supabase } from '../lib/supabase';

const CATEGORIES = [
  { value: 'bug', label: '🐛 Bug Report', desc: 'Something isn\'t working correctly' },
  { value: 'feature', label: '💡 Feature Request', desc: 'An idea for something new' },
  { value: 'improvement', label: '🔧 Improvement', desc: 'Something that could work better' },
  { value: 'question', label: '❓ Question', desc: 'Need help understanding something' },
];

export function FeedbackPage({ profile }) {
  const [category, setCategory] = useState('bug');
  const [subject, setSubject] = useState('');
  const [details, setDetails] = useState('');
  const [page, setPage] = useState('');
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);

  async function handleSubmit() {
    if (!subject.trim()) return setError('Please enter a subject.');
    if (!details.trim()) return setError('Please describe the issue or suggestion.');
    setSaving(true);
    setError('');
    try {
      const { error: insertErr } = await supabase.from('feedback').insert({
        user_id: profile.id,
        user_name: profile.name,
        user_role: profile.role,
        user_email: profile.email,
        org_id: profile.org_id,
        category,
        subject: subject.trim(),
        details: details.trim(),
        page_reference: page.trim() || null,
        status: 'new',
      });
      if (insertErr) throw insertErr;
      setSuccess(true);
      setSubject('');
      setDetails('');
      setPage('');
      setCategory('bug');
      // Refresh history if shown
      if (showHistory) loadHistory();
    } catch (e) {
      setError(e.message || 'Failed to submit feedback');
    }
    setSaving(false);
  }

  async function loadHistory() {
    const { data } = await supabase
      .from('feedback')
      .select('*')
      .eq('user_id', profile.id)
      .order('created_at', { ascending: false });
    setHistory(data || []);
    setHistoryLoaded(true);
    setShowHistory(true);
  }

  function formatDate(d) {
    if (!d) return '';
    return new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  }

  const statusColors = {
    new: { bg: 'rgba(245,166,35,.1)', color: '#f5a623', label: 'New' },
    reviewing: { bg: 'rgba(59,130,246,.1)', color: '#3b82f6', label: 'Reviewing' },
    planned: { bg: 'rgba(139,92,246,.1)', color: '#8b5cf6', label: 'Planned' },
    resolved: { bg: 'rgba(39,174,96,.1)', color: '#27ae60', label: 'Resolved' },
    closed: { bg: 'rgba(107,114,128,.1)', color: '#6b7280', label: 'Closed' },
  };

  return (
    <div>
      <div className="page-hdr">
        <div>
          <div className="page-title">Feedback</div>
          <div className="page-sub">Report bugs, suggest features, or ask questions</div>
        </div>
      </div>

      {success ? (
        <div className="card" style={{ marginBottom: 20 }}>
          <div style={{ padding: 32, textAlign: 'center' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>Thanks for your feedback!</div>
            <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 20 }}>
              We'll review it and get back to you if needed.
            </div>
            <button className="btn" onClick={() => setSuccess(false)}>Submit Another</button>
          </div>
        </div>
      ) : (
        <div className="card" style={{ marginBottom: 20 }}>
          <div className="card-hdr"><span className="card-title">Submit Feedback</span></div>
          <div style={{ padding: 16 }}>

            {/* Category selector */}
            <div className="fgrp">
              <label className="flbl">Category</label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {CATEGORIES.map(c => (
                  <button
                    key={c.value}
                    onClick={() => setCategory(c.value)}
                    className={`btn xs ${category === c.value ? '' : 'outline'}`}
                    style={category === c.value ? {
                      background: 'var(--accent)', color: '#fff', borderColor: 'var(--accent)',
                    } : {}}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
              <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>
                {CATEGORIES.find(c => c.value === category)?.desc}
              </div>
            </div>

            {/* Subject */}
            <div className="fgrp">
              <label className="flbl">Subject *</label>
              <input
                className="finp"
                value={subject}
                onChange={e => setSubject(e.target.value)}
                placeholder={category === 'bug' ? 'e.g. Photos not uploading on Android' : 'e.g. Add ability to export evidence as CSV'}
              />
            </div>

            {/* Details */}
            <div className="fgrp">
              <label className="flbl">Details *</label>
              <textarea
                className="finp"
                rows={5}
                value={details}
                onChange={e => setDetails(e.target.value)}
                placeholder={category === 'bug'
                  ? 'What happened? What did you expect to happen? Does it happen every time?'
                  : 'Describe what you\'d like to see and how it would help you.'}
                style={{ resize: 'vertical' }}
              />
            </div>

            {/* Page reference */}
            <div className="fgrp">
              <label className="flbl">Which page were you on? (optional)</label>
              <select className="fsel" value={page} onChange={e => setPage(e.target.value)}>
                <option value="">Select a page...</option>
                <option value="Dashboard">Dashboard</option>
                <option value="Evidence Log">Evidence Log</option>
                <option value="Add Evidence">Add Evidence form</option>
                <option value="Unit Progress">Unit Progress</option>
                <option value="Trade School">Trade & School</option>
                <option value="Review Queue">Review Queue</option>
                <option value="Training Plans">Training Plans</option>
                <option value="Reports">Reports</option>
                <option value="Manage People">Manage People</option>
                <option value="Account">Account</option>
                <option value="Login">Login page</option>
                <option value="Other">Other</option>
              </select>
            </div>

            {error && <div className="err">{error}</div>}

            <div style={{ marginTop: 16 }}>
              <button className="btn" onClick={handleSubmit} disabled={saving}>
                {saving ? 'Submitting…' : 'Submit Feedback'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* History toggle */}
      <div className="card">
        <div className="card-hdr">
          <span className="card-title">My Feedback History</span>
          <button className="btn-sm" onClick={() => showHistory ? setShowHistory(false) : loadHistory()}>
            {showHistory ? 'Hide' : 'Show'}
          </button>
        </div>
        {showHistory && (
          history.length === 0 ? (
            <div style={{ padding: 20, textAlign: 'center', color: 'var(--muted)', fontSize: 12 }}>
              No feedback submitted yet.
            </div>
          ) : (
            <div style={{ padding: '4px 16px 12px' }}>
              {history.map(fb => {
                const st = statusColors[fb.status] || statusColors.new;
                return (
                  <div key={fb.id} style={{
                    padding: '10px 12px', marginBottom: 8,
                    border: '1px solid var(--border)', borderRadius: 6,
                    background: 'var(--panel)',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 11, fontWeight: 600 }}>
                        {CATEGORIES.find(c => c.value === fb.category)?.label || fb.category}
                      </span>
                      <span style={{
                        fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 3,
                        background: st.bg, color: st.color,
                      }}>{st.label}</span>
                      <span style={{ fontSize: 10, color: 'var(--muted)', marginLeft: 'auto' }}>
                        {formatDate(fb.created_at)}
                      </span>
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>{fb.subject}</div>
                    <div style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.5 }}>{fb.details}</div>
                    {fb.admin_response && (
                      <div style={{
                        marginTop: 8, padding: '8px 10px', borderRadius: 4,
                        background: 'rgba(39,174,96,.06)', borderLeft: '3px solid var(--green)',
                        fontSize: 12, color: 'var(--text)',
                      }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--green)', marginBottom: 2 }}>Response:</div>
                        {fb.admin_response}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )
        )}
      </div>
    </div>
  );
}
